<template>
  <div
    v-if="visible"
    class="badge"
    :class="classes">
    <slot :value="displayValue">
      {{ displayValue }}
    </slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults( defineProps<{
  /**
   * The value displayed in the badge.
   */
  value?: string | number;
  /**
   * The variant of the badge.
   */
  variant?: string;
  /**
   * The size of the badge.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Whether to show the badge when the value is zero.
   */
  showZero?: boolean;
  /**
   * Whether to show the badge when the value is empty.
   */
  showEmpty?: boolean;
  /**
   * The position of the badge.
   */
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";
  /**
   * The maximum value displayed in the badge. If the value exceeds this maximum, it will be displayed as `max+`.
   */
  max?: number | string;
}>(), {
  value: 0,
  variant: "primary",
  size: "md",
  showZero: false,
  showEmpty: false,
  position: "top-right",
  max: 99,
} );

/**
 * The classes to apply to the badge.
 */
const classes = computed( () => {
  const result = [
    `badge--${props.variant}`,
    `badge--${props.size}`,
    `badge--${props.position}`,
  ];

  if ( visible.value ) {
    result.push( "badge--visible" );
  }

  if ( overflowing.value ) {
    result.push( "badge--overflowing" );
  }

  if ( zero.value ) {
    result.push( "badge--zero" );
  }

  if ( empty.value ) {
    result.push( "badge--empty" );
  }

  return result;
} );

/**
 * Whether the badge should be visible.
 */
const visible = computed( () => {
  return !!props.value || ( props.showZero && props.value === 0 ) || ( props.showEmpty && !props.value );
} );

/**
 * Whether the badge is overflowing.
 */
const overflowing = computed( () => {
  return parseInt( `${props.value}`, 10 ) > parseInt( `${props.max}`, 10 );
} );

/**
 * Whether the badge value is zero.
 */
const zero = computed( () => {
  return props.value === 0;
} );

/**
 * Whether the badge value is empty.
 */
const empty = computed( () => {
  return !props.value;
} );

/**
 * Numeric version of the value
 */
const numericValue = computed<number>( () => {
  return parseInt( `${props.value}`, 10 );
} );

/**
 * The value to display in the badge.
 */
const displayValue = computed( () => {
  if ( numericValue.value < 0 ) {
    return 0;
  }

  if ( overflowing.value ) {
    return `${props.max}+`;
  }

  return props.value;
} );

const color = computed( () => {
  const variant = props.variant || "primary";
  return `var( --color-${variant}-500 )`;
} );

const contrastColor = computed( () => {
  const variant = props.variant || "primary";
  return `var( --color-${variant}-text )`;
} );
</script>

<style scoped lang="scss">
.badge {
  --color: v-bind(contrastColor);
  --color-background: v-bind(color);

  position: absolute;
  background: var( --color-background );
  color: var( --color );
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--s-2);
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  min-width: 2em;
  min-height: 2em;
  line-height: 2em;
  z-index: 99;
  user-select: none;

  &--xs {
    font-size: 0.4rem;
    padding: var(--s-1);
  }

  &--sm {
    font-size: 0.6rem;
  }

  &--lg {
    font-size: 1rem;
  }

  &--center {
    top: 50%;
    left: 50%;
    transform: translateX( -50% ) translateY( -50% );
  }

  &--top-right {
    top: 0;
    right: 0;
    transform: translateX( 50% ) translateY( -50% );
  }

  &--top-left {
    top: 0;
    left: 0;
    transform: translateX( -50% ) translateY( -50% );
  }

  &--bottom-left {
    bottom: 0;
    left: 0;
    transform: translateX( -50% ) translateY( 50% );
  }

  &--bottom-right {
    bottom: 0;
    right: 0;
    transform: translateX( 50% ) translateY( 50% );
  }
}
</style>
